import React, { useState } from 'react';
import supabase from '../config/supabase';
import { SUPABASE_URL } from '../config/database';

/**
 * EmailImportButton
 *
 * "Or import via email" CTA. Calls the request-email-import Edge Function for
 * the supplied store, which sends the signed-in user a styled email with a
 * single "Import items →" magic-link button. Clicking that button always
 * lands the user on /add-items.html (the upload screen) with the right store
 * auto-selected, so they can upload invoices, inventory lists, or any other
 * item lists from whatever device is most convenient.
 *
 * Used from the dashboard low-item-count setup card and the items page header.
 * The vanilla add-items.html implementation mirrors this flow inline because
 * that page is not a React surface.
 *
 * Props:
 *   storeId: number (required) — store to import items into
 *   userEmail: string — used in the confirmation copy ("we sent it to ...")
 *   tone: 'amber' | 'plain' — visual treatment
 */
export default function EmailImportButton({
  storeId,
  userEmail,
  tone = 'amber',
}) {
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');

  const send = async (resend = false) => {
    if (!storeId) {
      setError('No store selected.');
      setStatus('error');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess?.session?.access_token;
      if (!accessToken) throw new Error('Not signed in.');
      const resolvedReturnPath = '/add-items.html';
      const res = await fetch(`${SUPABASE_URL}/functions/v1/request-email-import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          store_id: storeId,
          resend,
          return_path: resolvedReturnPath,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || !payload.ok) {
        throw new Error(payload.error || `Request failed (${res.status}).`);
      }
      setSentTo(payload.sent_to || userEmail || '');
      setStatus('sent');
    } catch (err) {
      setError(err?.message || String(err));
      setStatus('error');
    }
  };

  const accent = tone === 'amber' ? '#8B6135' : '#5A4A42';
  const border = tone === 'amber' ? '#E8CEAF' : '#D8C8B8';

  if (status === 'sent') {
    return (
      <div style={{
        marginTop: '0.45rem',
        background: '#FFFDF7',
        border: `1px dashed ${border}`,
        borderRadius: '8px',
        padding: '0.55rem 0.7rem',
        fontSize: '0.92rem',
        color: '#5A4A42',
        lineHeight: 1.5,
      }}>
        ✉️ We sent an email to <strong>{sentTo}</strong>.
        Open it on whatever device is easiest and click <strong>Import items</strong> —
        the link takes you straight to the upload screen.
        {' '}
        <button
          type="button"
          onClick={() => send(true)}
          style={{
            border: 'none', background: 'none', color: accent,
            fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: '0.92rem',
            textDecoration: 'underline',
          }}
        >Resend email</button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: '0.45rem' }}>
      <button
        type="button"
        onClick={() => send(false)}
        disabled={status === 'sending'}
        style={{
          fontSize: '0.85rem',
          fontWeight: 700,
          color: accent,
          background: '#fff',
          border: `1px solid ${border}`,
          borderRadius: '999px',
          padding: '0.25rem 0.75rem',
          cursor: status === 'sending' ? 'wait' : 'pointer',
        }}
      >
        {status === 'sending' ? 'Sending email…' : 'Or email me a link to continue later →'}
      </button>
      {status === 'error' && (
        <span style={{ marginLeft: '0.5rem', color: '#B03A48', fontSize: '0.85rem' }}>
          {error}
        </span>
      )}
    </div>
  );
}
